// src/components/EntityDetailSheet.tsx
//
// Modal shown when the user taps any interactive chip in chat. Renders
// a Google-Place-style card: hero photo carousel, action buttons
// (Directions / Call / Website / Open in Maps / Ask in Chat), key
// metadata (rating, price, hours), and an AI-generated summary.
//
// Works for any entity category — if it isn't a physical place we
// just hide the place-specific rows and show the summary.

import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Rating,
  Skeleton,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Close,
  Directions,
  Phone,
  Language,
  Map as MapIcon,
  Chat,
  AccessTime,
  Place as PlaceIcon,
  Person,
  Event,
  Architecture,
  School,
  History,
  Info,
  ExpandMore,
  ExpandLess,
  ChevronLeft,
  ChevronRight,
  RefreshRounded,
  DirectionsWalk,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material';
import {
  useEntityDetail,
  EntityDetailPayload,
} from '../hooks/useEntityDetail';
import { InteractiveElement } from '../utils/interactiveContent';
import { useIsPinned, usePinActions } from '../hooks/usePinned';
import { useT } from '../i18n/useT';

interface Props {
  open: boolean;
  onClose: () => void;
  element: InteractiveElement | null;
  /** Caller's current location for Places proximity bias. Optional. */
  userLocation?: { lat: number; lng: number };
  /** What the chat was discussing when the chip was tapped. Helps the AI summary stay on-topic. */
  conversationContext?: string;
  /** Back-compat: forward to chat when the user taps "Ask in Chat". */
  onAskInChat?: (message: string, element: InteractiveElement) => void;
}

const CATEGORY_ICON: Record<string, React.ElementType> = {
  place: PlaceIcon,
  person: Person,
  event: Event,
  architecture: Architecture,
  concept: School,
  period: History,
  other: Info,
};

function priceSymbol(n?: number): string {
  if (typeof n !== 'number') return '';
  return '$'.repeat(Math.max(1, Math.min(4, n)));
}

export const EntityDetailSheet: React.FC<Props> = ({
  open,
  onClose,
  element,
  userLocation,
  conversationContext,
  onAskInChat,
}) => {
  // Build request params. When the dialog is closed we pass null so the
  // hook doesn't keep re-fetching, but we also want to avoid briefly
  // nulling on close animation — keep the last element around for that
  // rendering pass.
  const [lastElement, setLastElement] =
    useState<InteractiveElement | null>(null);
  useEffect(() => {
    if (element) setLastElement(element);
  }, [element]);

  const params = useMemo(
    () =>
      element
        ? {
            name: element.text,
            category: element.category,
            context: conversationContext,
            location: userLocation,
          }
        : null,
    [element, conversationContext, userLocation]
  );

  const { detail, loading, error } = useEntityDetail(params);

  const active = element || lastElement;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'hidden',
        },
      }}
    >
      {active && (
        <EntitySheetContent
          element={active}
          detail={detail}
          loading={loading}
          error={error}
          userLocation={userLocation}
          onClose={onClose}
          onAskInChat={onAskInChat}
        />
      )}
    </Dialog>
  );
};

const EntitySheetContent: React.FC<{
  element: InteractiveElement;
  detail: EntityDetailPayload | null;
  loading: boolean;
  error: string | null;
  userLocation?: { lat: number; lng: number };
  onClose: () => void;
  onAskInChat?: (message: string, element: InteractiveElement) => void;
}> = ({ element, detail, loading, error, userLocation, onClose, onAskInChat }) => {
  const t = useT();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  // Pinning hooks. Pin id is derived from category+identifier so the
  // same chip across different chats produces the same pin doc.
  const pinId = `entity_${element.category}_${element.identifier}`;
  const isPinned = useIsPinned(pinId);
  const { pin, unpin } = usePinActions();

  const Icon = CATEGORY_ICON[element.category] || Info;
  const photos = detail?.photos || [];
  const activePhoto = photos[photoIndex];

  // Reset to first photo when the detail changes
  useEffect(() => {
    setPhotoIndex(0);
    setHoursExpanded(false);
  }, [detail?.place_id, detail?.name]);

  // Keyboard navigation for photos
  useEffect(() => {
    if (photos.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setPhotoIndex((i) => (i - 1 + photos.length) % photos.length);
      } else if (e.key === 'ArrowRight') {
        setPhotoIndex((i) => (i + 1) % photos.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length]);

  const prevPhoto = () =>
    setPhotoIndex((i) => (i - 1 + photos.length) % photos.length);
  const nextPhoto = () => setPhotoIndex((i) => (i + 1) % photos.length);

  // Swipe handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || photos.length < 2) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta > 0) prevPhoto();
      else nextPhoto();
    }
    touchStartX.current = null;
  };

  // Walking distance: only compute if we have both a place location
  // and the user's location. Simple haversine + ~80m/min walk rate.
  const walkingInfo = useMemo(() => {
    if (!detail?.location || !userLocation) return null;
    const R = 6371e3;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const φ1 = toRad(userLocation.lat);
    const φ2 = toRad(detail.location.lat);
    const Δφ = toRad(detail.location.lat - userLocation.lat);
    const Δλ = toRad(detail.location.lng - userLocation.lng);
    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    const meters = 2 * R * Math.asin(Math.sqrt(a));
    const minutes = Math.max(1, Math.round(meters / 80));
    const distStr =
      meters < 1000 ? `${Math.round(meters)}m` : `${(meters / 1000).toFixed(1)}km`;
    return { distStr, minutes };
  }, [detail?.location, userLocation]);

  return (
    <>
      {/* Hero area: photo carousel if we have photos, or a gradient+icon header */}
      <Box
        ref={heroRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{ position: 'relative', bgcolor: 'action.hover', userSelect: 'none' }}
      >
        {photos.length > 0 && activePhoto ? (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16/10',
              backgroundImage: `url(${activePhoto.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transition: 'background-image 0.2s',
            }}
          />
        ) : loading ? (
          <Skeleton
            variant="rectangular"
            sx={{ width: '100%', aspectRatio: '16/10' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16/10',
              background:
                'linear-gradient(135deg, #f5f3f0 0%, #e6dfd3 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 72, opacity: 0.35 }} />
          </Box>
        )}

        {/* Left/right arrows on carousel (desktop) */}
        {photos.length > 1 && (
          <>
            <IconButton
              onClick={prevPhoto}
              aria-label={t("Previous photo")}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.4)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                display: { xs: 'none', sm: 'flex' },
              }}
              size="small"
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={nextPhoto}
              aria-label={t("Next photo")}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.4)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                display: { xs: 'none', sm: 'flex' },
              }}
              size="small"
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Photo counter + dots */}
        {photos.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {photos.map((_, i) => (
              <Box
                key={i}
                onClick={() => setPhotoIndex(i)}
                sx={{
                  width: i === photoIndex ? 16 : 6,
                  height: 6,
                  borderRadius: i === photoIndex ? 3 : '50%',
                  bgcolor: i === photoIndex ? 'white' : 'rgba(255,255,255,0.55)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Box>
        )}

        {/* Close button */}
        <IconButton
          onClick={onClose}
          aria-label={t("Close")}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255,255,255,0.9)',
            color: 'text.primary',
            '&:hover': { bgcolor: 'white' },
            boxShadow: 1,
          }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {/* Title + category chip */}
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {detail?.name || element.text}
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mt: 0.5, flexWrap: 'wrap' }}
            >
              <Chip
                icon={<Icon sx={{ fontSize: 14 }} />}
                label={element.category}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
              {detail?.rating !== undefined && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Rating
                    value={detail.rating}
                    precision={0.1}
                    readOnly
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {detail.rating.toFixed(1)}
                    {detail.user_ratings_total
                      ? ` (${detail.user_ratings_total})`
                      : ''}
                  </Typography>
                </Stack>
              )}
              {typeof detail?.price_level === 'number' && (
                <Typography variant="caption" color="text.secondary">
                  {priceSymbol(detail.price_level)}
                </Typography>
              )}
              {detail?.open_now !== undefined && (
                <Chip
                  label={detail.open_now ? 'Open now' : 'Closed'}
                  size="small"
                  color={detail.open_now ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Address + walking distance */}
        {(detail?.address || walkingInfo) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}
          >
            {detail?.address && (
              <Typography variant="body2" color="text.secondary">
                {detail.address}
              </Typography>
            )}
            {walkingInfo && (
              <Chip
                icon={<DirectionsWalk sx={{ fontSize: 14 }} />}
                label={`${walkingInfo.minutes} min · ${walkingInfo.distStr}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.72rem',
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            )}
          </Stack>
        )}

        {/* Action buttons row */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 1,
            mx: -1,
            px: 1,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {detail?.action_urls.directions && (
            <ActionButton
              icon={<Directions />}
              label={t("Directions")}
              href={detail.action_urls.directions}
            />
          )}
          {detail?.action_urls.call && (
            <ActionButton
              icon={<Phone />}
              label={t("Call")}
              href={detail.action_urls.call}
            />
          )}
          {detail?.action_urls.website && (
            <ActionButton
              icon={<Language />}
              label={t("Website")}
              href={detail.action_urls.website}
            />
          )}
          {detail?.action_urls.maps_open && (
            <ActionButton
              icon={<MapIcon />}
              label={t("Maps")}
              href={detail.action_urls.maps_open}
            />
          )}
          {/* Pin to map — only when this entity has a real location.
              Independent of matching/swiping; this is purely "save
              to my map" intent. */}
          {detail?.location && (
            <ActionButton
              icon={isPinned ? <PushPin /> : <PushPinOutlined />}
              label={isPinned ? 'Pinned' : 'Pin'}
              onClick={() => {
                if (isPinned) {
                  unpin(pinId);
                  return;
                }
                pin({
                  id: pinId,
                  title: detail.name || element.text,
                  description: detail.ai_summary || detail.editorial_summary,
                  image_url:
                    detail.photos && detail.photos[0]
                      ? detail.photos[0].url
                      : null,
                  location: {
                    lat: detail.location!.lat,
                    lng: detail.location!.lng,
                    address: detail.address,
                  },
                  place_id: undefined,
                  chat_prompt: `Tell me more about ${
                    detail.name || element.text
                  }.`,
                  source: 'chat',
                });
              }}
            />
          )}
          {onAskInChat && (
            <ActionButton
              icon={<Chat />}
              label={t("Ask in Chat")}
              onClick={() => {
                onAskInChat(
                  `Tell me more about ${element.text}.`,
                  element
                );
                onClose();
              }}
            />
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* AI summary */}
        {loading && !detail ? (
          <Box sx={{ py: 1 }}>
            <Skeleton variant="text" width="90%" height={20} />
            <Skeleton variant="text" width="95%" height={20} />
            <Skeleton variant="text" width="70%" height={20} />
          </Box>
        ) : error && !detail ? (
          <Stack spacing={1} sx={{ py: 1 }}>
            <Typography variant="body2" color="error">
              
              {t("Couldn't load details.")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {error}
            </Typography>
            <Button
              startIcon={<RefreshRounded fontSize="small" />}
              size="small"
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 0.5 }}
            >
              
              {t("Retry")}
            </Button>
          </Stack>
        ) : detail ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
          >
            {detail.ai_summary}
          </Typography>
        ) : null}

        {/* Hours (collapsed by default) */}
        {detail?.hours && detail.hours.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<AccessTime fontSize="small" />}
              endIcon={hoursExpanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setHoursExpanded(!hoursExpanded)}
              size="small"
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              
              {t("Opening hours")}
            </Button>
            <Collapse in={hoursExpanded}>
              <Box sx={{ pl: 4, pt: 0.5 }}>
                {detail.hours.map((h, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.5 }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Attribution — only show once we have data to attribute */}
        {detail && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              opacity: 0.7,
              fontSize: '0.68rem',
            }}
          >
            {detail.source === 'places_with_ai'
              ? 'Info from Google Places · summary by AI'
              : 'Summary generated by AI'}
          </Typography>
        )}
      </DialogContent>
    </>
  );
};

const ActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
}> = ({ icon, label, href, onClick }) => {
  const common = {
    variant: 'outlined' as const,
    size: 'small' as const,
    startIcon: icon,
    sx: {
      textTransform: 'none' as const,
      flexShrink: 0,
      borderRadius: 4,
      fontSize: '0.8rem',
      px: 1.5,
    },
  };
  if (href) {
    return (
      <Button
        {...common}
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </Button>
    );
  }
  return (
    <Button {...common} onClick={onClick}>
      {label}
    </Button>
  );
};
